import {Component, OnDestroy, OnInit} from '@angular/core';
import {ProjectPartnumbersService} from "@catmodels/project-partnumbers/services/project-partnumbers.service";
import {Observable, Subscription} from "rxjs";
import {NotificationsService, ProjectService} from "@catcore/services";
import {isNullOrUndefined} from "util";
import {FormArray, FormBuilder, FormGroup} from "@angular/forms";
import {ProjectAppSelectionsService} from "@catmodels/project-partnumbers/services/project-app-selections.service";
import {sortByLabelFunc} from "@catcore/utils";
import {AppSelection} from "@catmodels/project-partnumbers/types";
import {ModalService} from "@catmodules/widgets/slide-in-modal/services/modal.service";
import {AppSelectionsService} from "@catmodels/project-partnumbers/services/app-selections.service";
import {ModifyAppSelectionComponent} from "@catmodels/project-partnumbers/components/modify-app-selection/modify-app-selection.component";
import {GgwProfile} from "@catmodels/ggw-profiles/types";
import {GenericResourceService} from "@catfeatures/services/generic-resource.service";

@Component({
  selector: 'cat-project-app-selections',
  templateUrl: './project-app-selections.component.html',
  styleUrls: ['./project-app-selections.component.scss']
})
export class ProjectAppSelectionsComponent implements OnInit,  OnDestroy {

  private projectSubscription: Subscription;
  private appSelectionSubscription: Subscription;
  private project = null;
  private projectAppSelections = null;

  form: FormGroup;
  exForm: FormGroup;

  constructor(private projectService: ProjectService,
              private formBuilder: FormBuilder,
              private projectAppSelectionsService: ProjectAppSelectionsService,
              private resourceService: GenericResourceService,
              private appSelectionsService: AppSelectionsService,
              private notification: NotificationsService,
              private modalService: ModalService,
              private projectPartnumbersService: ProjectPartnumbersService) {
    this.form = this.formBuilder.group({
      id: [', []'],
      prefer_gstreamer_without_drm: ['', []],
      appSelections: this.formBuilder.array([]),
    });
    this.exForm = this.formBuilder.group({
      appSelections: this.formBuilder.array([]),
    });
  }

  ngOnInit() {
    // first get the current project
    this.projectSubscription = this.projectService.getCurrentProject()
      .subscribe((project) => {
        const isActiveProject = !isNullOrUndefined(project) && !isNullOrUndefined(project.id);
        if (isActiveProject) {
          this.project = project;
          this.projectPartnumbersService.setActive();
          this.updateAppSelections();
        }
      });
  }

  ngOnDestroy() {
    this.projectSubscription.unsubscribe();
    if (this.appSelectionSubscription) {
      this.appSelectionSubscription.unsubscribe();
    }
    this.projectPartnumbersService.setInActive();
  }

  items: AppSelection[] = [];
  tableItems: any[] = [];
  exTableItems: any[] = [];
  dataTableInitialized = false;

  columns = {
    appBundle: {field: 'appBundle', header: 'App Bundle'},
    unitPartNumberFilter: {field: 'unitPartNumberFilter', header: 'Unit P/N Filter'},
    appPackageVersion: {field: 'appPackageVersion', header: 'Selected Version'},
  };

  private updateAppSelections() {
    this.dataTableInitialized = false;
    if (this.appSelectionSubscription) {
      this.appSelectionSubscription.unsubscribe();
    }
    this.appSelectionSubscription = this.projectService.getAllAppSelections(this.project)
      .subscribe((appSelections) => {
        this.projectAppSelections = appSelections;
        this.form.patchValue({
          prefer_gstreamer_without_drm: appSelections.prefer_gstreamer_without_drm
        });
        this.items = this.safeGetAppSelections(this.projectAppSelections);
        this.createDataTableItems(this.items);
        this.dataTableInitialized = true;
      });
  }

  dataAvailable() {
    return !isNullOrUndefined(this.tableItems) && this.tableItems.length !== 0;
  }

  onFormSubmit(form, withAppSelections: boolean = true) {
    const requests = [];

    const formValue = form.value;
    if (withAppSelections) {
      requests.push(
        this.projectAppSelectionsService.update({
          id: this.projectAppSelections.id,
          prefer_gstreamer_without_drm: formValue.prefer_gstreamer_without_drm
        })
      );
    }

    let reqCount = 0;
    const formArray = form.controls['appSelections'] as FormArray;
    for (let i = 0; i < formArray.length; i++) {
      if (formArray.at(i).dirty) {
        const form = formArray.at(i);
        reqCount++;
        requests.push(this.appSelectionsService.update(form.value));
        formArray.at(i).markAsPristine();
      }
    }

    Observable.forkJoin(requests)
      .subscribe((multiple) => {
        this.notification.success('Update successful', 'Successfully updated ' + (reqCount > 0 ? reqCount + ' ' : '')  + 'app selections');
      })
    ;
  }

  onSubmit(event) {
    this.onFormSubmit(this.form);
  }

  onSubmitEx(event) {
    this.onFormSubmit(this.exForm, false);
  }

  private createDataTableItems(items: any[]) {
    this.tableItems = [];
    this.exTableItems = [];

    const formArray = this.form.controls['appSelections'] as FormArray;
    while (formArray.length !== 0) {
      formArray.removeAt(0);
    }
    const exFormArray = this.exForm.controls['appSelections'] as FormArray;
    while (exFormArray.length !== 0) {
      exFormArray.removeAt(0);
    }

    items.forEach((appSelection) => {
      const formControl = this.formBuilder.group({
        id: ['', []],
        unit_part_number_filter: ['', []],
        appPackageVersion: ['', []],
      });
      if (appSelection.exception) {
        exFormArray.push(formControl);
      } else {
        formArray.push(formControl);
      }
      formControl.get('id').setValue(appSelection.id);
      formControl.get('unit_part_number_filter').setValue(appSelection.unit_part_number_filter);
      formControl.get('appPackageVersion').setValue(appSelection._links.hasOwnProperty("app_package_version") ? appSelection._links.app_package_version.href : null);

      const data = this.createTableItem(appSelection, formControl);

      if (appSelection.exception) {
        this.exTableItems = [...this.exTableItems, data];
      } else {
        this.tableItems = [...this.tableItems, data];
      }
    });
  }

  private createTableItem(appSelection: AppSelection, formControl) {
    return {
      appSelection: appSelection,
      appBundle: appSelection._links.app_bundle.label,
      // todo: Remove embedded partnumber from api response and use link label (
      unitPartNumberFilter: appSelection.unit_part_number_filter,
      appPackageVersion: appSelection._links.hasOwnProperty("app_package_version") ? appSelection._links.app_package_version.label : null,
      options: this.buildAppOptions(appSelection),
      formControl: formControl
    };
  }

  private buildAppOptions(service) {
    const options = [{
      value: null, label: ''
    }];
    if (service._links.hasOwnProperty("app_package_versions")) {
      service._links.app_package_versions.forEach((version) => {
        options.push({value: version.href, label: version.label});
      });
    }
    options.sort(sortByLabelFunc);
    return options;
  }

  dataAvailableEx() {
    return !isNullOrUndefined(this.exTableItems) && this.exTableItems.length !== 0;
  }

  editButtonClick(rowData) {
    const appSelection = rowData.appSelection;
    this.openModalForAppSelection(appSelection);
  }

  openModalForAppSelection(appSelection = null) {
    const compRef = this.modalService.open(ModifyAppSelectionComponent);
    compRef.componentInstance.appSelection = appSelection;
    compRef.componentInstance.projectAppSelections = this.projectAppSelections;
    compRef.componentInstance.afterCreate.subscribe((newAppSelection) => this.afterAppSelectionCreate(newAppSelection));
    compRef.componentInstance.afterUpdate.subscribe((appSelection) => this.afterAppSelectionUpdate(appSelection));
    compRef.componentInstance.afterDelete.subscribe(() => this.afterAppSelectionDelete(appSelection));
    return compRef;
  }

  private afterAppSelectionCreate(appSelection: AppSelection) {
    console.log("Created app selection: ", appSelection);
    this.items.push(appSelection);
    this.createDataTableItems(this.items);
  }

  private afterAppSelectionUpdate(appSelection: AppSelection) {
    console.log("Updated app selection: ", appSelection);
    this.items.filter((a) => { return a.id === appSelection.id })
      .forEach((item: AppSelection) => {
        item.unit_part_number_filter = appSelection.unit_part_number_filter;
        item._links.app_package_version = appSelection._links.app_package_version;
      });
    this.createDataTableItems(this.items);
  }

  private afterAppSelectionDelete(appSelection: AppSelection) {
    const newItems = [...this.items];
    let index = newItems.findIndex((p) => p.id === appSelection.id);
    if (index >= 0) {
      newItems.splice(index, 1);
      this.items = newItems;
      this.createDataTableItems(this.items);
    }
  }

  private safeGetAppSelections(projectAppSelections) {
    if (projectAppSelections
        && projectAppSelections.hasOwnProperty("_embedded")
        && projectAppSelections._embedded.hasOwnProperty("app_selections")) {
      return projectAppSelections._embedded.app_selections;
    } else {
      return [];
    }
  }

  afterAppTemplateUpdate() {
    this.updateAppSelections();
  }

  onCreate() {
    this.openModalForAppSelection();
  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_develop@2/app/Resources/Public/src/app/modules/models/project-partnumbers/components/project-app-selections/project-app-selections.component.ts